import { ProFormInstance } from '@ant-design/pro-components';
import { isEqual } from 'lodash';
import { useMemo, useState } from 'react';
import { useError } from './useError';

export type UseFieldErrorType = {
  errors: Record<string, any>;
  reset: () => void;
  validateFields: (fields?: string[]) => void;
  setFieldErrors: (error: any) => void;
  getValidator: (
    name: string,
    title: string,
    transform?: (value: any) => any,
  ) => {
    validator: (rule: any, newValue: any) => Promise<void>;
  };
};

export const useFieldError = (
  form?: React.MutableRefObject<ProFormInstance<any> | undefined>,
  errorMapping?: Record<string, string>,
): UseFieldErrorType => {
  const [errors, setErrors] = useState<Record<string, any>>({});
  const errorMessages = useError();

  return useMemo(
    () => ({
      errors,
      reset: () => setErrors({}),
      validateFields: (fields?: string[]) => {
        form?.current?.validateFields(fields);
      },
      setFieldErrors: (errorObject: any) => {
        const error = errorObject.data?.error;
        const message = errorObject.data?.message ?? errorObject;
        let newErrors;

        if (errorMapping?.[error]) {
          newErrors = {
            [errorMapping?.[error]]: {
              msg: error,
            },
          };
        } else if (message && Array.isArray(message)) {
          newErrors = {};

          message.forEach((m: any) => {
            const field = m.split(' ')[0];
            const msg = m.split(' ').splice(1).join('_');

            newErrors[field] = { msg };
          });
        }

        if (newErrors) {
          setErrors(newErrors);
          form?.current?.validateFields();
        }
      },
      getValidator: (
        name: string,
        title: string,
        transform: (value: any) => any = (value) => value,
      ) => ({
        validator: async (rule: any, newValue: any): Promise<void> => {
          if (errors[name]) {
            const { msg, value: oldValue } = errors[name];

            if (!oldValue) {
              setErrors({
                ...errors,
                [name]: {
                  msg,
                  value: transform(newValue),
                },
              });
            }

            if (!oldValue || isEqual(oldValue, transform(newValue))) {
              throw new Error(errorMessages[msg]?.(title));
            }
          }
        },
      }),
    }),
    [JSON.stringify(errors), JSON.stringify(errorMapping), form],
  );
};
